import React, { useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { companyInfo } from '../data/mockData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactSection = ({ t, onOpenConsultationForm }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      if (response.data.status === 'success') {
        toast.success(response.data.message);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error(
        t.contactSection.form.submit === 'Envoyer'
          ? 'Erreur lors de l\'envoi du message'
          : 'Error sending message'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-[#F5F3F0]">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2
              className="text-4xl sm:text-5xl font-light text-[#2C2C2C] mb-6"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              {t.contactSection.title}
            </h2>
            <div className="w-24 h-1 bg-[#C4B5A0] mx-auto mb-6"></div>
            <p className="text-lg text-[#8B7D6B] leading-relaxed">
              {t.contactSection.subtitle}
            </p>
          </div>

          {/* Contact Information */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="text-center p-6 bg-white rounded-lg shadow-sm">
              <p className="text-sm uppercase tracking-wider text-[#C4B5A0] mb-2 font-medium">
                {t.footer.location.split(',')[0]}
              </p>
              <p className="text-[#2C2C2C] font-medium">{companyInfo.location}</p>
            </div>
            <div className="text-center p-6 bg-white rounded-lg shadow-sm">
              <p className="text-sm uppercase tracking-wider text-[#C4B5A0] mb-2 font-medium">Email</p>
              <a
                href={`mailto:${companyInfo.email}`}
                className="text-[#2C2C2C] hover:text-[#8B4513] transition-colors font-medium"
              >
                {companyInfo.email}
              </a>
            </div>
            <div className="text-center p-6 bg-white rounded-lg shadow-sm">
              <p className="text-sm uppercase tracking-wider text-[#C4B5A0] mb-2 font-medium">
                {t.contactSection.form.submit === 'Envoyer' ? 'Téléphone' : 'Phone'}
              </p>
              <a
                href={`tel:${companyInfo.phone}`}
                className="text-[#2C2C2C] hover:text-[#8B4513] transition-colors font-medium"
              >
                {companyInfo.phone}
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label
                  htmlFor="firstName"
                  className="block text-sm font-medium text-[#2C2C2C] mb-2"
                >
                  {t.contactSection.form.firstName}
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-[#F5F3F0] border border-[#D4C4B0] rounded-md text-[#2C2C2C] focus:outline-none focus:ring-2 focus:ring-[#C4B5A0] focus:border-transparent transition-all"
                />
              </div>
              <div>
                <label
                  htmlFor="lastName"
                  className="block text-sm font-medium text-[#2C2C2C] mb-2"
                >
                  {t.contactSection.form.lastName}
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-[#F5F3F0] border border-[#D4C4B0] rounded-md text-[#2C2C2C] focus:outline-none focus:ring-2 focus:ring-[#C4B5A0] focus:border-transparent transition-all"
                />
              </div>
            </div>

            <div className="mb-6">
              <label
                htmlFor="email"
                className="block text-sm font-medium text-[#2C2C2C] mb-2"
              >
                {t.contactSection.form.email}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-[#F5F3F0] border border-[#D4C4B0] rounded-md text-[#2C2C2C] focus:outline-none focus:ring-2 focus:ring-[#C4B5A0] focus:border-transparent transition-all"
              />
            </div>

            <div className="mb-6">
              <label
                htmlFor="message"
                className="block text-sm font-medium text-[#2C2C2C] mb-2"
              >
                {t.contactSection.form.message}
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={6}
                className="w-full px-4 py-3 bg-[#F5F3F0] border border-[#D4C4B0] rounded-md text-[#2C2C2C] focus:outline-none focus:ring-2 focus:ring-[#C4B5A0] focus:border-transparent transition-all resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-8 py-4 text-base font-medium tracking-wider uppercase text-white bg-[#8B4513] border-2 border-[#8B4513] rounded-md transition-all duration-300 hover:bg-[#A0522D] hover:border-[#A0522D] hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting
                ? t.contactSection.form.submit === 'Envoyer'
                  ? 'Envoi en cours...'
                  : 'Sending...'
                : t.contactSection.form.submit}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
